import moment from 'moment-jalaali';

// Ensure Persian locale and jalaali calendar are enabled
moment.loadPersian({ dialect: 'persian-modern', usePersianDigits: true });

const toPersianDigits = (str) => {
  if (str == null) return '';
  const map = {
    '0': '۰', '1': '۱', '2': '۲', '3': '۳', '4': '۴',
    '5': '۵', '6': '۶', '7': '۷', '8': '۸', '9': '۹'
  };
  return String(str).replace(/[0-9]/g, (d) => map[d]);
};

/** Convert Persian/Arabic-Indic digits to ASCII so ISO parsing never fails. */
export const toAsciiDigits = (str) => {
  if (str == null) return '';
  const map = {
    '۰': '0',
    '۱': '1',
    '۲': '2',
    '۳': '3',
    '۴': '4',
    '۵': '5',
    '۶': '6',
    '۷': '7',
    '۸': '8',
    '۹': '9',
    '٠': '0',
    '١': '1',
    '٢': '2',
    '٣': '3',
    '٤': '4',
    '٥': '5',
    '٦': '6',
    '٧': '7',
    '٨': '8',
    '٩': '9',
  };

  return String(str).replace(/[۰-۹٠-٩]/g, (d) => map[d] ?? d);
};

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Persist DatePicker / moment values as ASCII Gregorian YYYY-MM-DD.
 * Critical: with usePersianDigits=true, bare format('YYYY-MM-DD') can emit
 * Persian digits or NaN fragments that later render as 0000/NaN/NaN.
 */
export const momentToIsoDate = (value) => {
  if (value == null || value === '') return null;

  try {
    if (typeof value === 'string') {
      const ascii = toAsciiDigits(value).trim();
      const match = ascii.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (match) return match[0];
    }

    let m;
    if (moment.isMoment(value)) {
      m = value;
    } else if (value?.toDate) {
      m = moment(value.toDate());
    } else {
      m = moment(value);
    }

    if (!m || !m.isValid()) return null;

    // Guard against half-typed DatePicker values that look "valid" but break Jalali tokens.
    const jy = m.jYear?.();
    const jm = m.jMonth?.();
    const jd = m.jDate?.();
    if ([jy, jm, jd].some((n) => n != null && Number.isNaN(Number(n)))) return null;

    const iso = toAsciiDigits(m.clone().locale('en').format('YYYY-MM-DD'));
    return ISO_DATE_RE.test(iso) ? iso : null;
  } catch (error) {
    console.error('Error in momentToIsoDate:', error, 'Value:', value);
    return null;
  }
};

/**
 * Parse URL/API ISO date into a Moment suitable for DesktopDatePicker.
 * Returns null for empty/invalid values so the picker shows the placeholder
 * instead of 0000/NaN/NaN.
 */
export const parseIsoDateToMoment = (value) => {
  if (value == null || value === '') return null;

  try {
    const ascii = toAsciiDigits(String(value)).trim();
    const match = ascii.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;

    const m = moment(match[0], 'YYYY-MM-DD', true);
    if (!m.isValid()) return null;

    const jy = m.jYear();
    const jm = m.jMonth();
    const jd = m.jDate();
    if ([jy, jm, jd].some((n) => Number.isNaN(Number(n)))) return null;

    return m;
  } catch (error) {
    console.error('Error in parseIsoDateToMoment:', error, 'Value:', value);
    return null;
  }
};

export const formatDate = (value) => {
  if (!value) return '';
  const m = moment(value);
  const y = m.format('jYYYY');
  const mo = m.format('jMM');
  const d = m.format('jDD');
  return `j${y}/j${mo}/j${d}`;
};

// Display-friendly Jalali date without literal 'j' prefixes
export const formatJalaliDateDisplay = (value) => {
  if (!value) return '';
  let m;
  
  try {
    if (typeof value === 'string') {
      // Try different date formats
      if (value.includes('/')) {
        // Try Jalali format first
        m = moment(value, 'jYYYY/jMM/jDD', true);
        if (!m.isValid()) {
          // Try Gregorian format
          m = moment(value, 'YYYY/MM/DD', true);
        }
      } else if (value.includes('-')) {
        // If it's already an ISO date coming from backend, treat it as Gregorian.
        // (moment-jalaali can otherwise interpret tokens in Jalali context)
        const ascii = toAsciiDigits(value);
        const isoMatch = ascii.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (isoMatch) {
          m = moment(isoMatch[0], 'YYYY-MM-DD', true);
        } else {
          // Fallback: let moment try to parse it
          m = moment(ascii);
        }
      } else {
        m = moment(value);
      }
    } else if (moment.isMoment(value)) {
      m = value;
    } else if (value?.toDate) {
      m = moment(value.toDate());
    } else {
      m = moment(value);
    }
    
    if (!m || !m.isValid()) {
      console.warn('Invalid date value:', value);
      return '';
    }
    
    // Convert to Jalali and format
    const jalaliDate = m.format('jYYYY/jMM/jDD');
    return toPersianDigits(jalaliDate);
  } catch (error) {
    console.error('Error formatting date:', error, 'Value:', value);
    return '';
  }
};

export const formatDateTime = (value) => {
  if (!value) return '';
  return moment(value).format('jYYYY/jMM/jDD HH:mm');
};

export const toGregorianISO = (value) => {
  if (!value) return null;

  const fromMoment = momentToIsoDate(value);
  if (fromMoment) return fromMoment;

  try {
    if (typeof value === 'string') {
      const ascii = toAsciiDigits(value).trim();
      if (ascii.includes('/')) {
        let m = moment(ascii, 'jYYYY/jMM/jDD', true);
        if (!m.isValid()) m = moment(ascii, 'YYYY/MM/DD', true);
        return momentToIsoDate(m);
      }
    }
    return null;
  } catch (error) {
    console.error('Error converting date to Gregorian:', error, 'Value:', value);
    return null;
  }
};

// Helper function to convert Jalali date to Gregorian
export const jalaliToGregorian = (jalaliDate) => {
  if (!jalaliDate) return null;
  
  try {
    let m;
    
    if (moment.isMoment(jalaliDate)) {
      m = jalaliDate;
    } else if (typeof jalaliDate === 'string') {
      // Parse as Jalali date
      m = moment(jalaliDate, 'jYYYY/jMM/jDD', true);
      if (!m.isValid()) {
        m = moment(jalaliDate, 'jYYYY-jMM-jDD', true);
      }
    } else {
      m = moment(jalaliDate);
    }
    
    if (!m || !m.isValid()) {
      return null;
    }
    
    return momentToIsoDate(m);
  } catch (error) {
    console.error('Error converting Jalali to Gregorian:', error);
    return null;
  }
};

// Simple date conversion function
export const convertToGregorian = (dateValue) => momentToIsoDate(dateValue);

// Ultra-simple date conversion - just get the date and format it
export const simpleDateConversion = (dateValue) => {
  if (!dateValue) return null;
  
  try {
    // Get the actual date object
    let dateObj;
    
    if (moment.isMoment(dateValue)) {
      dateObj = dateValue.toDate();
    } else {
      dateObj = new Date(dateValue);
    }
    
    // Check if it's a valid date
    if (isNaN(dateObj.getTime())) {
      return null;
    }
    
    // Format as YYYY-MM-DD
    // Use UTC to avoid timezone shifting across day boundaries.
    const year = dateObj.getUTCFullYear();
    const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getUTCDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error in simpleDateConversion:', error);
    return null;
  }
};

/**
 * برای فیلتر: تاریخ را به YYYY-MM-DD تبدیل می‌کند
 * - رشته API مثل "2025-02-12" را مستقیم برمی‌گرداند
 * - moment از DatePicker را تبدیل می‌کند
 */
export const getDateStrForFilter = (value) => {
  if (value == null || value === '') return null;
  const iso = momentToIsoDate(value);
  if (iso) return iso;
  if (typeof value === 'string') {
    const ascii = toAsciiDigits(value).trim();
    const m = ascii.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return m[0];
  }
  return simpleDateConversion(value);
};

// Debug helper function
export const debugDate = (value, label = 'Date') => {
  console.log(`${label}:`, {
    original: value,
    type: typeof value,
    isMoment: moment.isMoment(value),
    momentValue: moment.isMoment(value) ? value.format() : moment(value).format(),
    jalaliDisplay: formatJalaliDateDisplay(value),
    gregorianISO: toGregorianISO(value),
    jalaliToGregorian: jalaliToGregorian(value),
    convertToGregorian: convertToGregorian(value)
  });
};


